import { filesize } from 'filesize';

export type CreativeType = 'image' | 'video' | 'animated-image';

interface MediaSize {
    width: number;
    height: number;
}

export interface CreativeLimitations {
    // These fields are validated during upload
    maxFileSize: number; // in bytes
    recommendedSizes?: MediaSize[];
    aspectRatios?: MediaSize[];

    // These fields are not yet validated, only displayed
    maxDuration?: number; // in seconds
    duration?: string;
    frames?: string;
    loops?: string;
}

export const CREATIVE_LIMITATIONS: Record<CreativeType, CreativeLimitations> = {
    image: {
        maxFileSize: 10 * 1024 * 1024, // 10 MB
        recommendedSizes: [
            { width: 1200, height: 628 },
            { width: 1080, height: 1080 },
            { width: 600, height: 400 },
        ],
    },
    video: {
        maxFileSize: 1 * 1024 * 1024 * 1024, // 1 GB
        aspectRatios: [
            { width: 1, height: 1 }, // 1:1
            { width: 16, height: 9 }, // 16:9
            { width: 9, height: 16 }, // 9:16
        ],
        maxDuration: 600, // 10 minutes
    },
    'animated-image': {
        maxFileSize: 300 * 1024, // 300 KB
        recommendedSizes: [{ width: 600, height: 400 }],
        duration: '1-4 seconds',
        frames: '5-20',
        loops: '1-4',
    },
};

export function getCreativeType(file: File): CreativeType {
    const mimeType = file.type.toLowerCase();

    // Check for APNG (animated PNG)
    if (mimeType === 'image/apng' || (mimeType === 'image/png' && file.name.toLowerCase().endsWith('.apng'))) {
        return 'animated-image';
    }

    // Check for video
    if (mimeType.startsWith('video/')) {
        return 'video';
    }

    // Default to image
    return 'image';
}

async function getMediaSize(file: File): Promise<MediaSize> {
    return new Promise((resolve, reject) => {
        if (file.type.startsWith('image/')) {
            const img = new Image();
            const objectUrl = URL.createObjectURL(file);

            img.onload = () => {
                URL.revokeObjectURL(objectUrl);
                resolve({
                    width: img.naturalWidth,
                    height: img.naturalHeight,
                });
            };

            img.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error(`Failed to load image: ${file.name}`));
            };

            img.src = objectUrl;
        } else if (file.type.startsWith('video/')) {
            const video = document.createElement('video');
            const objectUrl = URL.createObjectURL(file);

            video.onloadedmetadata = () => {
                URL.revokeObjectURL(objectUrl);
                resolve({
                    width: video.videoWidth,
                    height: video.videoHeight,
                });
            };

            video.onerror = () => {
                URL.revokeObjectURL(objectUrl);
                reject(new Error(`Failed to load video: ${file.name}`));
            };

            video.src = objectUrl;
            video.preload = 'metadata';
        } else {
            reject(new Error(`Unsupported file type: ${file.type}`));
        }
    });
}

export interface FileValidationResult {
    errors: string[];
    warnings: string[];
}

export async function validateCreativeFile(file: File): Promise<FileValidationResult> {
    const creativeType = getCreativeType(file);
    const limits = CREATIVE_LIMITATIONS[creativeType];
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate file size (hard limit)
    if (file.size > limits.maxFileSize) {
        errors.push(
            `File size (${filesize(file.size, { standard: 'jedec' })}) exceeds the maximum allowed size of ${filesize(limits.maxFileSize, { standard: 'jedec' })} for ${creativeType}s.`,
        );
    }

    // Check dimensions and aspect ratios for images and videos
    if (limits.recommendedSizes || limits.aspectRatios) {
        try {
            const mediaSize = await getMediaSize(file);

            // Check recommended sizes
            if (limits.recommendedSizes) {
                const matchesRecommended = limits.recommendedSizes.some(
                    (recommendedSize) =>
                        recommendedSize.width === mediaSize.width && recommendedSize.height === mediaSize.height,
                );

                if (!matchesRecommended) {
                    const recommendedSizesStr = limits.recommendedSizes
                        .map((size) => `${size.width}x${size.height}`)
                        .join(', ');
                    warnings.push(
                        `Dimensions ${mediaSize.width}x${mediaSize.height} do not match recommended sizes: ${recommendedSizesStr}`,
                    );
                }
            }

            // Check aspect ratios
            if (limits.aspectRatios) {
                const hasMatchingAspectRatio = limits.aspectRatios.some(
                    (ratio) => mediaSize.width * ratio.height === mediaSize.height * ratio.width,
                );

                if (!hasMatchingAspectRatio) {
                    const aspectRatiosStr = limits.aspectRatios
                        .map((ratio) => `${ratio.width}:${ratio.height}`)
                        .join(', ');
                    warnings.push(`Aspect ratio does not match recommended ratios: ${aspectRatiosStr}`);
                }
            }
        } catch (error) {
            // If we can't load dimensions, just skip the check
            console.warn(`Could not validate dimensions for ${file.name}:`, error);
        }
    }

    return {
        errors,
        warnings,
    };
}
